import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import { Ref, useImperativeHandle, useMemo, useRef } from 'react';

import { useCheckSpotOnly } from '@flipster/features/src/shared/regulation';
import { useSymbols } from '@flipster/features/src/shared/symbol/use-symbols';
import { NavigationHeightVariableKey } from '@flipster/react/panda/globals/vars';
import { css } from '@flipster/styled-system/css';
import { flex, hstack } from '@flipster/styled-system/patterns';

import { marketPageBunja } from '../state';
import { Title } from '../title';
import { MarketSearchTab } from './market-search-tab';
import { SearchInput } from './search-input';

export interface MarketTabRef {
  getClientHeight: () => number;
}

interface MarketTabProps {
  ref?: Ref<MarketTabRef>;
  scrollToList: () => void;
}

export const MarketTab = ({ ref, scrollToList }: MarketTabProps) => {
  const t = useTranslations();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputBarRef = useRef<HTMLDivElement>(null);

  const { searchModeAtom, searchCursorAtom, setSearchCursorQuery } = useBunja(marketPageBunja);
  const { query: search } = useAtomValue(searchCursorAtom);
  const isSearchMode = useAtomValue(searchModeAtom);
  const { totalSymbol } = useSymbols();
  const isSpotOnly = useCheckSpotOnly();

  useImperativeHandle(ref, () => ({
    getClientHeight: () => {
      const wrapperHeight = wrapperRef.current?.getBoundingClientRect().height || 0;
      if (wrapperHeight > 0) return wrapperHeight;
      return inputBarRef.current?.getBoundingClientRect().height || 0;
    },
  }));

  // 검색 모드에서는 검색 스코프 탭을, 그 외에는 "Market" 타이틀 + Perpetual/Spot 토글을 노출한다.
  const tab = useMemo(() => {
    if (isSearchMode && !isSpotOnly) return <MarketSearchTab />;
    return <Title />;
  }, [isSearchMode, isSpotOnly]);

  return (
    <div
      ref={wrapperRef}
      className={css(
        flex.raw({
          flexDirection: { base: 'column', tablet: 'row' },
          pt: 8,
          pb: { tablet: 8 },
          align: { tablet: 'center' },
          gap: 8,
        }),
        {
          display: { base: 'contents', tablet: 'flex' },
          position: { tablet: 'sticky' },
          zIndex: { tablet: 4 },
          backgroundColor: { tablet: 'background.default.level-0' },
        },
      )}
      style={{ top: `calc(var(${NavigationHeightVariableKey}) - 1px)` }}
      id='market-tab'
    >
      {tab}
      <div
        ref={inputBarRef}
        className={css(
          hstack.raw({ justifyContent: 'flex-end', gap: 8 }),
          {
            position: { base: 'sticky', tablet: 'static' },
            zIndex: { base: 4, tablet: 'auto' },
            backgroundColor: { base: 'background.default.level-0', tablet: 'transparent' },
            pt: { base: 12, tablet: 0 },
            px: { base: 16, tablet: 0 },
            pb: { base: 16, tablet: 0 },
            ml: { base: 0, tablet: 'auto' },
            w: { tablet: 480 },
          },
          isSearchMode ? { order: { base: -1, tablet: 0 } } : {},
        )}
        style={{ top: `calc(var(${NavigationHeightVariableKey}) - 1px)` }}
      >
        <SearchInput
          value={search}
          onChange={(e) => {
            const next = e.target.value;
            const isEnteringSearchMode = !search && next.length > 0;
            setSearchCursorQuery(next);
            if (isEnteringSearchMode) requestAnimationFrame(() => scrollToList());
          }}
          onClear={() => setSearchCursorQuery('')}
          placeholder={t('Market.r250925.Search_count_selections', { count: totalSymbol })}
        />
      </div>
    </div>
  );
};
